import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  NgModule,
  ErrorHandler,
  CUSTOM_ELEMENTS_SCHEMA,
  isDevMode,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule } from '@angular/router';
import {
  CompositePropagatorModule,
  OTEL_INSTRUMENTATION_PLUGINS,
  OtelColExporterModule,
  OtelWebTracerModule,
} from '@jufab/opentelemetry-angular-interceptor';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';

import {
  AccordionComponent,
  AccordionItemComponent,
  AppTitleBarComponent,
  AudioPlayerComponent,
  BasicTileComponent,
  CalendarComponent,
  CheckboxComponent,
  ColorPickerComponent,
  CondensedExperienceCardComponent,
  CustomPillComponent,
  DatePickerComponent,
  DefaultButtonComponent,
  DrawerComponent,
  DropdownComponent,
  EditableTextComponent,
  FABComponent,
  GenericModalComponent,
  HorizontalTabBarComponent,
  IconButtonComponent,
  InputFieldComponent,
  LeftRightPickerComponent,
  ListComponent,
  ListItemComponent,
  PopoutComponent,
  RadioComponent,
  SearchBoxComponent,
  SentimentButtonComponent,
  SidePanelComponent,
  SliderComponent,
  SnackBarComponent,
  TabBarComponent,
  TextAreaComponent,
  ToggleSwitchComponent,
  ToggleTextComponent,
  ToolTipContentComponent,
  VerticalTabBarComponent,
  WarningPopupComponent,
  XIAnimationComponent,
  XIIconComponent,
  XISVGComponent,
  runAllComponentsOutsideAngular,
} from '@inmoment/the-kitchen';

import { environment } from 'environments/environment';
import { initConfig } from './app-config';
import { AppSharedModule } from './app-shared.module';
import { AppComponent } from './app.component';

import { DownloadCenterComponent } from './components/download-center/download-center.component';
import { MobileModeSelectorComponent } from './components/mobile-mode-selector/mobile-mode-selector.component';
import { SpotlightAssistantComponent } from './components/spotlight-assistant/spotlight-assistant.component';
import { UikitModule } from './components/uikit/uikit.module';
import { AccountModule } from './modules/account/account.module';
import { LoginComponent } from './modules/account/pages/login/login.component';
import { ActionPlanModule } from './modules/action-plan/action-plan.module';
import { CollectionModule } from './modules/collection/collection.module';
import { ConfigurationModule } from './modules/configuration/configuration.module';
import { ConfigurationRouteModule } from './modules/configuration-route/configuration-route.module';
import { DashboardModule } from './modules/dashboard/dashboard.module';
import { FieldModule } from './modules/field/field.module';
import { FullStoryModule } from './modules/full-story/full-story.module';
import { GroupModule } from './modules/group/group.module';
import { ProjectModule } from './modules/project/project.module';
import { UploadModule } from './modules/upload/upload.module';
import { UserModule } from './modules/user/user.module';
import { WidgetModule } from './modules/widget/widget.module';
import { NotFoundComponent } from './not-found/not-found.component';
import { AnalyticsService } from './providers/analytics.service';
import { GlobalErrorHandler } from './providers/global-error-handler';
import { HttpErrorInterceptor } from './providers/http-error.interceptor';

import { HttpMutatingOperationsInterceptor } from './providers/http-mutating-operations.interceptor';
import { initTelemetry, OpenTelemetryService } from './services/otel.service';
import { PreviousRouteService } from './services/previous-route.service';

const env = (window as any).env ?? environment;
@NgModule({
  declarations: [AppComponent, NotFoundComponent, DownloadCenterComponent],
  bootstrap: [AppComponent],
  imports: [
    MobileModeSelectorComponent,
    SpotlightAssistantComponent,
    RouterModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppSharedModule,
    AccountModule,
    FieldModule,
    ProjectModule,
    CollectionModule,
    DashboardModule,
    UploadModule,
    WidgetModule,
    ConfigurationModule,
    ConfigurationRouteModule,
    UserModule,
    GroupModule,
    UikitModule,
    ActionPlanModule,
    RouterModule.forRoot(
      [
        { path: 'login', component: LoginComponent },
        { path: '', pathMatch: 'full', redirectTo: 'login' },
        { path: '**', pathMatch: 'full', component: NotFoundComponent },
      ],
      {}
    ),
    LoggerModule.forRoot({ level: NgxLoggerLevel.DEBUG }),
    FullStoryModule.forRoot({
      appName: 'lexalytics',
      environment: env.name,
      devMode: ['local', 'dev', 'preprod'].includes(env.name.toLowerCase()),
      ...(env.fullStory ?? {}),
    }),
    OtelColExporterModule,
    CompositePropagatorModule,
    OtelWebTracerModule.forRoot({
      commonConfig: {
        ...environment.openTelemetry.commonConfig,
        production: !isDevMode(),
      },
      otelcolConfig: environment.openTelemetry.otelcolConfig,
    }),
  ],
  providers: [
    provideAppInitializer(() => {
      const initializerFn = initConfig(inject(HttpClient));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initTelemetry();
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpMutatingOperationsInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    AnalyticsService,
    PreviousRouteService,
    OpenTelemetryService,
    {
      provide: OTEL_INSTRUMENTATION_PLUGINS,
      useValue: [],
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
  constructor(
    // initialization at entry point is required for first use
    previousRouteService: PreviousRouteService,
    router: Router
  ) {
    runAllComponentsOutsideAngular();
    HorizontalTabBarComponent.define();
    AppTitleBarComponent.define();
    TabBarComponent.define();
    SnackBarComponent.define();
    ToolTipContentComponent.define();
    XIIconComponent.define();
    IconButtonComponent.define();
    SentimentButtonComponent.define();
    XIAnimationComponent.define();
    GenericModalComponent.define();
    BasicTileComponent.define();
    WarningPopupComponent.define();
    DefaultButtonComponent.define();
    XISVGComponent.define();
    ToggleSwitchComponent.define();
    ToggleTextComponent.define();
    DropdownComponent.define();
    SidePanelComponent.define();
    VerticalTabBarComponent.define();
    ListComponent.define();
    ListItemComponent.define();
    SearchBoxComponent.define();
    DrawerComponent.define();
    FABComponent.define();
    TextAreaComponent.define();
    InputFieldComponent.define();
    ColorPickerComponent.define();
    CustomPillComponent.define();
    RadioComponent.define();
    AccordionComponent.define();
    AccordionItemComponent.define();
    CheckboxComponent.define();
    DatePickerComponent.define();
    SliderComponent.define();
    CalendarComponent.define();
    PopoutComponent.define();
    LeftRightPickerComponent.define();
    AudioPlayerComponent.define();
    EditableTextComponent.define();
    CondensedExperienceCardComponent.define();
    new OpenTelemetryService().enableRouterTelemetry(router);
  }
}
